import React, { useMemo, useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useAppSelector } from "../../../hooks/hooks";
import { fetchAxiosToken } from "../../../helpers/fetchAxiosToken";
import { NavbarHotel } from "../NavbarHotel";
import { NavbarMobileHotel } from "../NavbarMobileHotel";
import { NavbarBroker } from "pages/dashboardBroker/NavbarBroker";
import { NavbarMobileBroker } from "pages/dashboardBroker/NavbarMobileBroker";

type Form = {
    usuario_niubiz: string;
    password_niubiz: string;
    id_comercio_soles: string; // usamos string para validar/mostrar sin parseos
    id_comercio_dolares: string;
};

const INITIAL: Form = {
    usuario_niubiz: "",
    password_niubiz: "",
    id_comercio_soles: "",
    id_comercio_dolares: "",
};

const CONTROL =
    "w-full h-10 px-3 rounded-lg border border-[#E9E9E9] bg-white outline-none text-sm " +
    "focus:ring-2 focus:ring-[#1C4CBB]/40 focus:border-[#1C4CBB]";

export const DatosNiubizHotel: React.FC = () => {
    const { id: id_usuario, tipo_usuario } = useAppSelector(
        (s) => s.usuario_logeado,
    );

    const [form, setForm] = useState<Form>(INITIAL);
    const [saving, setSaving] = useState(false);
    const [showClave, setShowClave] = useState(false);

    const endpoint = useMemo(
        () => `/usuarios/updateDatosNiubiz/${id_usuario}`,
        [id_usuario],
    );

    useEffect(() => {
        (async () => {
            try {
                const data = await fetchAxiosToken({
                    url: `/usuarios/getDatosNiubiz/${id_usuario}`,
                });

                if (data) {
                    setForm({
                        usuario_niubiz: data.datos_niubiz.usuario_niubiz,
                        password_niubiz:
                            data.datos_niubiz.password_niubiz ?? "",
                        id_comercio_soles:
                            data.datos_niubiz.id_comercio_soles ?? "",
                        id_comercio_dolares:
                            data.datos_niubiz.id_comercio_dolares ?? "",
                    });
                }
            } catch {}
        })();
    }, [id_usuario]);

    const onChange = <K extends keyof Form>(k: K, v: Form[K]) =>
        setForm((p) => ({ ...p, [k]: v }));

    const validate = () => {
        if (!form.usuario_niubiz.trim()) return "El usuario es requerido.";
        if (!form.password_niubiz.trim()) return "El password es requerido";

        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Dato inválido",
                text: err,
                icon: "error",
            });
            return;
        }

        try {
            setSaving(true);
            // Body según especificación
            const body = {
                usuario_niubiz: form.usuario_niubiz,
                password_niubiz: form.password_niubiz,
                id_comercio_soles: form.id_comercio_soles,
                id_comercio_dolares: form.id_comercio_dolares,
            };

            await fetchAxiosToken({
                url: endpoint,
                method: "put",
                body,
            });

            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Datos guardados",
                text: "Los datos de configuración de niubiz se guardo correctamente.",
                icon: "success",
            });
        } catch (e: any) {
            const msg =
                e?.response?.data?.message ??
                "No se pudo guardar la configuración.";
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text: String(msg),
                icon: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const Page = (
        <div className="flex">
            <div className="w-full mt-6">
                <div className="grid w-11/12 max-w-6xl gap-6 mx-auto lg:grid-cols-2">
                    {/* Intro */}
                    <div className="text-[#4D4D4D]">
                        <p className="text-xl font-bold 2xl:text-2xl">
                            Datos de Niubiz
                        </p>
                        <p className="mt-4 text-sm 2xl:text-base">
                            Conecta tu hotel con multiples pasarelas de pagos.
                            Los campos con * son obligatorios.
                        </p>
                        <div className="p-4 mt-4 border rounded-lg bg-[#FAFAFA] text-sm">
                            <p className="font-medium">¿Qué necesitas?</p>
                            <ul className="pl-5 mt-2 list-disc">
                                <li>
                                    usuario y contraseña proporcionada por el
                                    proveedor.
                                </li>
                                <li>
                                    Id de comercio de soles si tu hotel cobra en
                                    Soles.
                                </li>
                                <li>
                                    Id de comercio en dolares si tu hotel cobra
                                    en Dolares.
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Formulario */}
                    <div className="w-full p-4 border border-[#CCCCCC] rounded-lg shadow-lg">
                        <h3 className="mb-3 text-base font-semibold text-[#4D4D4D]">
                            Configurar datos de Niubiz
                        </h3>

                        {/* Bloque: Integración */}
                        <div className="p-3 mb-4 rounded-lg bg-[#F8FAFF] border border-[#E6ECFF]">
                            <p className="mb-3 text-sm font-medium text-[#1C4CBB]">
                                Datos de integración
                            </p>

                            <div className="grid grid-cols-1 gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-[#3C3C3C]">
                                        Usuario *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className={`${CONTROL} pr-10`}
                                            value={form.usuario_niubiz}
                                            onChange={(e) =>
                                                onChange(
                                                    "usuario_niubiz",
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="integracion@gmail.com"
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-[#3C3C3C]">
                                        Password *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={
                                                showClave ? "text" : "password"
                                            }
                                            className={CONTROL}
                                            value={form.password_niubiz}
                                            onChange={(e) =>
                                                onChange(
                                                    "password_niubiz",
                                                    e.target.value,
                                                )
                                            }
                                            placeholder="*******"
                                        />
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowClave((s) => !s)
                                            }
                                            className="absolute inset-y-0 right-2 text-xs text-[#1C4CBB]"
                                        >
                                            {showClave ? "Ocultar" : "Mostrar"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mt-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-[#3C3C3C]">
                                        ID de comercio soles
                                    </label>
                                    <input
                                        className={CONTROL}
                                        value={form.id_comercio_soles}
                                        onChange={(e) =>
                                            onChange(
                                                "id_comercio_soles",
                                                e.target.value
                                                    .replace(/[^0-9]/g, "")
                                                    .slice(0, 11),
                                            )
                                        }
                                        placeholder="2061********"
                                        inputMode="numeric"
                                    />
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label className="text-sm font-medium text-[#3C3C3C]">
                                        ID de comercio dolares
                                    </label>
                                    <input
                                        className={CONTROL}
                                        value={form.id_comercio_dolares}
                                        onChange={(e) =>
                                            onChange(
                                                "id_comercio_dolares",
                                                e.target.value
                                                    .replace(/[^0-9]/g, "")
                                                    .slice(0, 11),
                                            )
                                        }
                                        placeholder="2061********"
                                        inputMode="numeric"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="h-11 px-6 rounded-lg text-white bg-[#029BFC] hover:opacity-90 disabled:opacity-60"
                            >
                                {saving
                                    ? "Guardando..."
                                    : "Guardar configuración"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Exclusivo para HOTEL (tipo_usuario === 8)
    if (tipo_usuario !== 8 && tipo_usuario !== 6) return null;

    const EsBroker = tipo_usuario === 6;

    return (
        <>
            {EsBroker ? (
                <NavbarBroker titulo="Pasarela de pagos Niubiz">
                    {Page}
                    <NavbarMobileBroker />
                </NavbarBroker>
            ) : (
                <NavbarHotel titulo="Pasarela de pagos Niubiz">
                    {Page}
                    <NavbarMobileHotel />
                </NavbarHotel>
            )}
        </>
    );
};

export default DatosNiubizHotel;
